import { AnimatedNumbers } from "components/DesignSystem/AnimatedNumbers/AnimatedNumbers";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import {
  JOURNAL_ENTRY_STATUS,
  JOURNAL_ENTRY_STATUS_DISPLAY,
  JOURNAL_ENTRY_STATUS_TYPE,
} from "constants/journalEntrySources";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useToast } from "hooks/useToast";
import { useModal } from "hooks/useModal";
import { useMemo } from "react";
import { Table } from "react-table-8.10.7";
import type { JournalEntry } from "types/Models/journalEntry";
import { useBulkUpdateJournalEntryStatusMutation } from "store/apis/journalEntry";
import BulkJEDeleteModal from "./BulkJEDeleteModal";
import JEStatusUpdateModal from "./JEStatusUpdateModal";
import Modal from "components/DesignSystem/Modal/Modal";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";

type TableSelectionControlsProps = {
  table: Table<JournalEntry>;
  groupId: string;
  entityId: string;
};

export type SelectedRow = {
  uuid: string;
  is_recurring_enabled: boolean;
  linked_payroll_id: string;
  status: string;
};

export default function TableSelectionControls({
  table,
  groupId,
  entityId,
}: TableSelectionControlsProps) {
  const selectedRows = table.getState().rowSelection;
  const deleteModal = useModal();
  const archiveModal = useModal();
  const markActiveModal = useModal();
  const { alertToast, successToast } = useToast();

  const [bulkUpdateStatus, { isLoading: isUpdatingStatus }] =
    useBulkUpdateJournalEntryStatusMutation();

  const allSelectedRows: SelectedRow[] = useMemo(
    () =>
      Object.entries(selectedRows)
        .filter(([_, value]) => value)
        .map(([key]) => JSON.parse(key)),
    [selectedRows]
  );

  const subtotal = useMemo(
    () =>
      table
        .getSelectedRowModel()
        .flatRows.reduce((sum, row) => sum + (row.original.amount || 0), 0),
    [table, selectedRows]
  );

  const allActive = allSelectedRows.every(
    (row) => row.status === JOURNAL_ENTRY_STATUS.ACTIVE
  );
  const allArchived = allSelectedRows.every(
    (row) => row.status === JOURNAL_ENTRY_STATUS.ARCHIVED
  );

  const entriesLabel = pluralize(
    allSelectedRows.length,
    "journal entry",
    "journal entries"
  );
  const archiveDescription = `You are about to archive ${entriesLabel}. You can mark them as active again later.`;
  const markActiveDescription = `You are about to mark ${entriesLabel} as active.`;

  const handleStatusUpdate = async (
    status: JOURNAL_ENTRY_STATUS_TYPE,
    onClose: () => void
  ) => {
    try {
      await bulkUpdateStatus({
        groupId,
        entityId,
        ledger_entry_uuids: allSelectedRows.map((r) => r.uuid),
        status,
      }).unwrap();
      successToast({
        message: `${pluralize(
          allSelectedRows.length,
          "journal entry",
          "journal entries"
        )} ${JOURNAL_ENTRY_STATUS_DISPLAY[status]}`,
      });
      onClose();
      table.resetRowSelection();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError).data?.error?.message ||
          "Failed to update journal entries.",
      });
    }
  };

  return (
    <SelectedFloatingControl>
      {allSelectedRows.length > 0 && (
        <>
          <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
            {allSelectedRows.length} selected
          </span>
          <span className="t-text-neutral-10 t-h-full">
            <DividerLine />
          </span>
          {subtotal > 0 && (
            <>
              <div className="t-flex t-items-center t-gap-1 t-text-text-60 t-text-subtext">
                <span>Subtotal:</span>
                <AnimatedNumbers amount={subtotal} />
              </div>
              <span className="t-text-neutral-10 t-h-full">
                <DividerLine />
              </span>
            </>
          )}
          {allActive && (
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
              blockedUi={<LockedButton text="Archive" />}
            >
              <Modal.Root
                open={archiveModal.isOpen}
                onOpenChange={archiveModal.toggle}
              >
                <Modal.Trigger asChild>
                  <Button size="small">Archive</Button>
                </Modal.Trigger>
                <Modal.Content>
                  <JEStatusUpdateModal
                    title="Archive Journal Entries?"
                    description={archiveDescription}
                    confirmLabel="Archive"
                    onConfirm={() =>
                      handleStatusUpdate(
                        JOURNAL_ENTRY_STATUS.ARCHIVED,
                        archiveModal.close
                      )
                    }
                    isLoading={isUpdatingStatus}
                  />
                </Modal.Content>
              </Modal.Root>
            </LockedUi>
          )}
          {allArchived && (
            <LockedUi
              feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
              blockedUi={<LockedButton text="Mark as active" />}
            >
              <Modal.Root
                open={markActiveModal.isOpen}
                onOpenChange={markActiveModal.toggle}
              >
                <Modal.Trigger asChild>
                  <Button size="small">Mark as active</Button>
                </Modal.Trigger>
                <Modal.Content>
                  <JEStatusUpdateModal
                    title="Mark Journal Entries as Active?"
                    description={markActiveDescription}
                    confirmLabel="Mark as active"
                    onConfirm={() =>
                      handleStatusUpdate(
                        JOURNAL_ENTRY_STATUS.ACTIVE,
                        markActiveModal.close
                      )
                    }
                    isLoading={isUpdatingStatus}
                  />
                </Modal.Content>
              </Modal.Root>
            </LockedUi>
          )}
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
            blockedUi={<LockedButton text="Delete" />}
          >
            <Modal.Root
              open={deleteModal.isOpen}
              onOpenChange={deleteModal.toggle}
            >
              <Modal.Trigger asChild>
                <Button size="small">Delete</Button>
              </Modal.Trigger>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Delete Journal Entries?</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <BulkJEDeleteModal
                  {...deleteModal}
                  allSelectedRows={allSelectedRows}
                  entityId={entityId}
                  groupId={groupId}
                  onSuccess={table.resetRowSelection}
                />
              </Modal.Content>
            </Modal.Root>
          </LockedUi>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={() => table.resetRowSelection()}
          >
            <Cross />
          </Button>
        </>
      )}
    </SelectedFloatingControl>
  );
}
